import React, { useEffect, useRef, useState } from 'react'
import { Drawer, Modal, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faClipboardList,
  faSparkles,
} from '@fortawesome/pro-regular-svg-icons'
import {
  useLabraChatContext,
  useLabraChatDispatch,
  useLabraChatSelector,
} from '../../modules/labraChat/context'
import {
  closeLabraChat,
  loadLabraChatSavedPrompts,
  openLabraChatEmailPreview,
  sendLabraChatPrompt,
  setLabraChatListOrigin,
  setLabraChatOpenList,
  startNewLabraChat,
} from '../../modules/labraChat/actions'
import {
  selectLabraChatLayout,
  selectLabraChatListOrigin,
  selectLabraChatOpenList,
  selectIsLabraChatPanelOpen,
  selectIsLabraChatFullScreen,
  selectIsLabraChatHistoryOpen,
  selectLabraChatMessages,
  selectLabraChatPreviewDocument,
  selectLabraChatPreviewEmail,
} from '../../modules/labraChat/selectors'
import { useStyles } from './LabraAIChatPanel.styles'
import { ChatHeader } from './ChatHeader/ChatHeader'
import { ChatHistoryView } from './ChatHistoryView/ChatHistoryView'
import { SuggestionChips } from './SuggestionChips/SuggestionChips'
import { ChatThread } from './ChatThread/ChatThread'
import { ChatInputBar } from './ChatInputBar/ChatInputBar'
import { PageContextPill } from './PageContextPill/PageContextPill'
import { ChatDocumentPreview } from './ChatDocumentPreview/ChatDocumentPreview'
import { ChatEmailPreviewModal } from './ChatEmailPreview/ChatEmailPreviewModal'
import {
  ChatRecommendationsByProduct,
  ChatRecommendations,
} from './ChatRecommendations/ChatRecommendations'
import { LABRA_CHAT_SCROLL_ID } from './constants'
import { useHasChatDock } from './dockContext'

export { LABRA_CHAT_SCROLL_ID }

const DEFAULT_SPLIT_PERCENT = 50

const MIN_SPLIT_PERCENT = 25
const MAX_SPLIT_PERCENT = 75
const SPLIT_KEYBOARD_STEP = 5

export interface LabraAIChatPanelProps {
  /** Agent name shown in the title bar (defaults to "Labra AI Agent"). */
  title?: string
  header?: React.ReactNode
  userName?: string
  /** Name of the page the agent will be told about, e.g. "Outgoing
   * referrals". Omit to send no page context and hide the pill. */
  pageContextLabel?: string
  /** When true, hides the expand / push-back control in the title bar. */
  hideFullScreenToggle?: boolean
  /** When true, draws a themed border around the drawer paper. */
  showPanelBorder?: boolean
  onAgentActivities?: () => void
  /** Open items waiting on the user; omit or zero to leave the count unsaid. */
  todoCount?: number
  /** Recommendations waiting on the user; omit or zero to leave it unsaid. */
  recommendationCount?: number
  /** Shown when the user opens the list from the counts, keyed by product. */
  recommendations?: ChatRecommendationsByProduct
  /** Omit when everything is listed; the "More recommendations" link hides. */
  onLoadMoreRecommendations?: () => void
  /** True while another page of recommendations is being fetched. */
  isLoadingMoreRecommendations?: boolean
  /** The to-do list behind the other count, same shape as recommendations. */
  todos?: ChatRecommendationsByProduct
  /** Omit when everything is listed; the "More to-dos" link hides. */
  onLoadMoreTodos?: () => void
  /** True while another page of to-dos is being fetched. */
  isLoadingMoreTodos?: boolean
  /**
   * 'drawer' anchors a half-viewport drawer to the right. 'floating' is a fixed
   * card over the page. 'embedded' fills the whole host viewport, for hosts that
   * already own it such as the Salesforce Canvas iframe, where a drawer would
   * leave half the frame empty.
   *
   * 'docked' is the only one that is not a modal: it renders in flow, in the
   * space the host gives it, so the user can keep working in the page while the
   * chat is open. The others render in a portal and take focus, which no amount
   * of host CSS can undo.
   */
  variant?: 'drawer' | 'floating' | 'embedded' | 'docked'
  /**
   * Replaces the default empty-state greeting/counts when there are no messages.
   * Used by AI skills/schedules create (sample prompts).
   */
  emptyContent?: React.ReactNode
  /** When true with emptyContent, skip the Hi {name} greeting and todo counts. */
  hideDefaultEmpty?: boolean
  /** Overrides the composer placeholder (e.g. create-schedule copy). */
  composerPlaceholder?: string
}

export const LabraAIChatPanel: React.FC<LabraAIChatPanelProps> = ({
  title,
  header,
  userName,
  pageContextLabel,
  hideFullScreenToggle = false,
  showPanelBorder = false,
  variant = 'drawer',
  onAgentActivities,
  todoCount = 0,
  recommendationCount = 0,
  recommendations = {},
  onLoadMoreRecommendations,
  isLoadingMoreRecommendations = false,
  todos = {},
  onLoadMoreTodos,
  isLoadingMoreTodos = false,
  emptyContent,
  hideDefaultEmpty = false,
  composerPlaceholder,
}) => {
  const { classes, cx } = useStyles()
  const dispatch = useLabraChatDispatch()
  const { partnerId } = useLabraChatContext()

  const isPanelOpen = useLabraChatSelector(selectIsLabraChatPanelOpen)
  const isFullScreen = useLabraChatSelector(selectIsLabraChatFullScreen)
  const isHistoryOpen = useLabraChatSelector(selectIsLabraChatHistoryOpen)
  const messages = useLabraChatSelector(selectLabraChatMessages)
  const previewDocument = useLabraChatSelector(selectLabraChatPreviewDocument)
  const previewEmail = useLabraChatSelector(selectLabraChatPreviewEmail)
  const chosenLayout = useLabraChatSelector(selectLabraChatLayout)
  // A sidebar needs a column to sit in; without a dock it would fill the page.
  const hasDock = useHasChatDock()
  const hasMessages = messages.length > 0
  const showEmailPreview = isFullScreen && !!previewEmail
  const showPreview =
    showEmailPreview || (isFullScreen && !!previewDocument)

  const splitRowRef = useRef<HTMLDivElement>(null)
  const [splitPercent, setSplitPercent] = useState(DEFAULT_SPLIT_PERCENT)
  const [isDragging, setIsDragging] = useState(false)
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false)
  // In the slice, not component state: changing layout swaps the panel between
  // a drawer, a card and a docked column, and a remount would lose the view.
  const openList = useLabraChatSelector(selectLabraChatOpenList)
  const listOrigin = useLabraChatSelector(selectLabraChatListOrigin)

  // Pull the saved prompts in with the panel, so the composer's list is already
  // populated the first time it is opened. The thunk ignores repeat calls.
  useEffect(() => {
    if (isPanelOpen) dispatch(loadLabraChatSavedPrompts(partnerId))
  }, [isPanelOpen, partnerId, dispatch])

  const clampSplit = (value: number) =>
    Math.min(MAX_SPLIT_PERCENT, Math.max(MIN_SPLIT_PERCENT, value))

  // The host's variant holds until the user picks a layout from the header.
  const layout =
    chosenLayout ??
    (variant === 'docked' ? 'sidebar' : isFullScreen ? 'expanded' : 'floating')
  const placement = chosenLayout
    ? chosenLayout === 'sidebar'
      ? hasDock
        ? 'docked'
        : variant
      : chosenLayout === 'floating'
        ? 'floating'
        : variant
    : variant

  const handleClose = () => {
    dispatch(closeLabraChat())
  }

  /** Preview NBA opens the modal; other chips send as usual. */
  const handlePromptSelect = (prompt: string) => {
    const previewPrefix = '__labra_email_preview__:'
    if (prompt.startsWith(previewPrefix)) {
      const draftId = prompt.slice(previewPrefix.length)
      const match = [...messages]
        .reverse()
        .find(m => m.emailPreview?.draftId === draftId)?.emailPreview
      const fallback = [...messages]
        .reverse()
        .find(m => m.emailPreview)?.emailPreview
      const preview = match || fallback
      if (preview) {
        dispatch(openLabraChatEmailPreview(preview))
        return
      }
    }
    dispatch(sendLabraChatPrompt(partnerId, prompt))
  }

  const counts = [
    todoCount > 0 && {
      count: todoCount,
      noun: 'to-do',
      list: 'todos' as const,
    },
    recommendationCount > 0 && {
      count: recommendationCount,
      noun: 'recommendation',
      list: 'recommendations' as const,
    },
  ].filter(Boolean) as {
    count: number
    noun: string
    list: 'todos' | 'recommendations'
  }[]

  const conversation = (
    <div
      className={cx(
        classes.body,
        !hasMessages && classes.bodyCentered,
        showPreview && classes.bodyInSplit
      )}
    >
      {hasMessages && (
        <button
          className={`${classes.backButton} chatBackButton`}
          type="button"
          onClick={() =>
            dispatch(
              listOrigin
                ? setLabraChatOpenList(listOrigin)
                : startNewLabraChat()
            )
          }
          aria-label={listOrigin ? 'Back to list' : 'Back to start'}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}

      <div
        className={cx(
          classes.scrollArea,
          !hasMessages && classes.scrollAreaCentered
        )}
        id={LABRA_CHAT_SCROLL_ID}
      >
        {hasMessages ? (
          <ChatThread />
        ) : (
          <>
            {header}
            {emptyContent}
            {!hideDefaultEmpty && (
              <>
                {userName && (
                  <Typography className={classes.greeting}>
                    Hi {userName} 👋
                  </Typography>
                )}
                {counts.length > 0 && (
                  <Typography component="span" className={classes.summary}>
                    <span className={classes.dot} />
                    You have{' '}
                    {counts.map(({ count, noun, list }, index) => (
                      <React.Fragment key={noun}>
                        {index > 0 && ' and '}
                        <button
                          type="button"
                          className={classes.count}
                          onClick={() => dispatch(setLabraChatOpenList(list))}
                        >
                          {count} {noun}
                          {count === 1 ? '' : 's'}
                        </button>
                      </React.Fragment>
                    ))}{' '}
                    to catch up on, or start a new conversation.
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div
        className={cx(
          classes.bottomSection,
          !hasMessages && classes.bottomSectionCentered
        )}
      >
        {hasMessages && (
          <SuggestionChips onSelect={handlePromptSelect} singleLine />
        )}
        <PageContextPill label={pageContextLabel} />
        <div className={classes.composerGroup}>
          <ChatInputBar
            compact={hasMessages}
            placeholder={composerPlaceholder}
          />
          <Typography className={classes.disclaimer}>
            Labra AI can make mistakes. Please double-check responses.
          </Typography>
        </div>
      </div>
    </div>
  )

  const content = (
    <>
      <ChatHeader
        title={title}
        hideFullScreenToggle={hideFullScreenToggle}
        layout={layout}
        canDock={hasDock}
        onAgentActivities={onAgentActivities}
      />

      {isHistoryOpen ? (
        <ChatHistoryView isFullScreen={isFullScreen} />
      ) : openList ? (
        <ChatRecommendations
          title={openList === 'todos' ? 'To-dos' : 'Recommendations'}
          icon={openList === 'todos' ? faClipboardList : faSparkles}
          iconGradient={openList === 'recommendations'}
          moreLabel={
            openList === 'todos' ? 'More to-dos' : 'More recommendations'
          }
          recommendations={openList === 'todos' ? todos : recommendations}
          // Up the path, not sideways: the list sits above the conversation, so
          // leaving it returns to the welcome. `startNewLabraChat` clears the
          // thread, which also clears the list and its origin.
          onBack={() => dispatch(startNewLabraChat())}
          onSelect={prompt => {
            // Remember where this conversation started, so its back control
            // returns to the list rather than the welcome.
            dispatch(setLabraChatListOrigin(openList))
            dispatch(setLabraChatOpenList(null))
            handlePromptSelect(prompt)
          }}
          onLoadMore={
            openList === 'todos' ? onLoadMoreTodos : onLoadMoreRecommendations
          }
          isLoadingMore={
            openList === 'todos'
              ? isLoadingMoreTodos
              : isLoadingMoreRecommendations
          }
        />
      ) : showPreview ? (
        <div
          ref={splitRowRef}
          className={classes.splitRow}
          style={{ '--chat-split': `${splitPercent}%` } as React.CSSProperties}
        >
          {!isPreviewExpanded && conversation}
          {!isPreviewExpanded && (
            <div
              className={classes.resizeHandle}
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={Math.round(splitPercent)}
              aria-valuemin={MIN_SPLIT_PERCENT}
              aria-valuemax={MAX_SPLIT_PERCENT}
              aria-label="Resize conversation and preview"
              tabIndex={0}
              onPointerDown={event => {
                event.currentTarget.setPointerCapture(event.pointerId)
                setIsDragging(true)
              }}
              onPointerMove={event => {
                if (!isDragging) return
                const rect = splitRowRef.current?.getBoundingClientRect()
                if (!rect) return
                setSplitPercent(
                  clampSplit(((event.clientX - rect.left) / rect.width) * 100)
                )
              }}
              onPointerUp={event => {
                event.currentTarget.releasePointerCapture(event.pointerId)
                setIsDragging(false)
              }}
              onKeyDown={event => {
                if (event.key === 'ArrowLeft')
                  setSplitPercent(
                    clampSplit(splitPercent - SPLIT_KEYBOARD_STEP)
                  )
                if (event.key === 'ArrowRight')
                  setSplitPercent(
                    clampSplit(splitPercent + SPLIT_KEYBOARD_STEP)
                  )
              }}
            />
          )}
          {showEmailPreview && previewEmail ? (
            <ChatEmailPreviewModal
              preview={previewEmail}
              isExpanded={isPreviewExpanded}
              onToggleExpanded={() =>
                setIsPreviewExpanded(current => !current)
              }
              className={cx(
                classes.previewPane,
                isDragging && classes.previewPaneDragging
              )}
            />
          ) : previewDocument ? (
            <ChatDocumentPreview
              document={previewDocument}
              isExpanded={isPreviewExpanded}
              onToggleExpanded={() =>
                setIsPreviewExpanded(current => !current)
              }
              className={cx(
                classes.previewPane,
                isDragging && classes.previewPaneDragging
              )}
            />
          ) : null}
        </div>
      ) : (
        conversation
      )}
    </>
  )

  if (isFullScreen) {
    return (
      <Modal open={isPanelOpen} onClose={handleClose}>
        <div className={classes.modalCard}>{content}</div>
      </Modal>
    )
  }

  if (!isPanelOpen && (placement === 'floating' || placement === 'docked')) {
    return null
  }

  if (placement === 'floating') {
    return <div className={classes.floatingCard}>{content}</div>
  }

  if (placement === 'docked') {
    return (
      <aside
        className={cx(
          classes.dockedPanel,
          showPanelBorder && classes.borderedPaper
        )}
      >
        {content}
      </aside>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={isPanelOpen}
      onClose={handleClose}
      variant="temporary"
      elevation={8}
      PaperProps={{
        className: cx(
          placement === 'embedded' ? classes.embeddedPaper : classes.paper,
          showPanelBorder && classes.borderedPaper
        ),
      }}
    >
      {content}
    </Drawer>
  )
}
